import { SponsoredPlacementsProvider } from '@pata-app/ui-sponsored-placements';

import React, { Suspense, lazy } from 'react';
import { Route, Routes } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';

import LoadingIndicator from '../components/loadingIndicator/loadingIndicator';
import { ServerRenderContext } from '../contexts/ssr.context';
import AddReview from '../pages/businessProfile/components/businessReviews/addReview/addReview';
import { Terms } from '../pages/businessProfile/components/terms/terms';
import { PreviewModeContextProvider } from '../pages/businessProfile/context/preview-mode.context';
import '../styles/fonts.css';
import GlobalStyle from '../styles/globalStyles';
import { appTheme } from '../styles/theme';

const BusinessHome = lazy(() => import(/* webpackChunkName: "business-home" */ '../pages/businessHome/businessHome'));
const BusinessProfile = lazy(
  () => import(/* webpackChunkName: "business-profile" */ '../pages/businessProfile/businessProfile'),
);
const Search = lazy(() => import(/* webpackChunkName: "search" */ '../pages/search/search'));

interface IApp {
  dataProps?: any;
}

const ACTIVE_CONFIG = {
  version: 1,
  updatedAt: '2026-02-11T10:00:00Z',
  items: [
    {
      id: 'home-sponsored-active',
      displaySection: 'home.list.sponsored_tile',
      startAt: '2026-01-01T00:00:00Z',
      endAt: '2026-12-31T23:59:59Z',
      creative: {
        type: 'image',
        images: {
          desktop: {
            url: 'https://placehold.co/640x700/117191/ffffff?text=Home+Page+Tile+Desktop',
            alt: 'Desktop sponsored creative',
          },
          mobile: {
            url: 'https://placehold.co/640x700/8cc4b8/ffffff?text=Home+Page+Tile+Mobile',
            alt: 'Mobile sponsored creative',
          },
        },
        linkUrl: 'https://example.com/sponsored',
      },
    },
    {
      id: 'home-sponsored-active',
      displaySection: 'home.section.sponsored_section',
      startAt: '2026-01-01T00:00:00Z',
      endAt: '2026-12-31T23:59:59Z',
      creative: {
        type: 'image',
        images: {
          desktop: {
            url: 'https://placehold.co/1200x250/117191/ffffff?text=Home+Page+Section+Desktop',
            alt: 'Desktop sponsored creative',
          },
          mobile: {
            url: 'https://placehold.co/640x700/8cc4b8/ffffff?text=Home+Page+Tile+Mobile',
            alt: 'Mobile sponsored creative',
          },
        },
        linkUrl: 'https://example.com/sponsored',
      },
    },
    {
      id: 'home-sponsored-active',
      displaySection: 'search.results.sponsored_tile',
      startAt: '2026-01-01T00:00:00Z',
      endAt: '2026-12-31T23:59:59Z',
      creative: {
        type: 'image',
        images: {
          desktop: {
            url: 'https://placehold.co/450x300/117191/ffffff?text=Business+Search+Tile+Desktop',
            alt: 'Desktop sponsored creative',
          },
          mobile: {
            url: 'https://placehold.co/450x300/8cc4b8/ffffff?text=Business+Search+Tile+Mobile',
            alt: 'Mobile sponsored creative',
          },
        },
        linkUrl: 'https://example.com/sponsored',
      },
    },
    {
      id: 'home-sponsored-active',
      displaySection: 'profile.section.sponsored_section',
      startAt: '2026-01-01T00:00:00Z',
      endAt: '2026-12-31T23:59:59Z',
      creative: {
        type: 'image',
        images: {
          desktop: {
            url: 'https://placehold.co/1200x250/117191/ffffff?text=Business+Profile+Section+Desktop',
            alt: 'Desktop sponsored creative',
          },
          mobile: {
            url: 'https://placehold.co/640x700/8cc4b8/ffffff?text=Business+Profile+Section+Mobile',
            alt: 'Mobile sponsored creative',
          },
        },
        linkUrl: 'https://example.com/sponsored',
      },
    },
  ],
};

const toDataUrl = (value: unknown): string => {
  return `data:application/json,${encodeURIComponent(JSON.stringify(value))}`;
};

const App = ({ dataProps }: IApp) => {
  return (
    <ServerRenderContext.Provider value={dataProps}>
      <ThemeProvider theme={appTheme}>
        <SponsoredPlacementsProvider configUrl={process.env.SPONSORED_PLACEMENT_CONFIG_URL}>
          <GlobalStyle />
          <Suspense fallback={<LoadingIndicator />}>
            <Routes>
              <Route path="/p" element={<BusinessHome />} />
              <Route path="/p/search" element={<Search />} />
              <Route path="/p/search/:id" element={<Search />} />
              <Route
                path="/p/:id/*"
                element={
                  <PreviewModeContextProvider>
                    <BusinessProfile />
                  </PreviewModeContextProvider>
                }
              />
              <Route path="/p/:id/terms" element={<Terms />} />
              <Route path="/p/:id/review/:reviewToken" element={<AddReview />} />
              <Route path="/" element={<BusinessHome />} />
            </Routes>
          </Suspense>
        </SponsoredPlacementsProvider>
      </ThemeProvider>
    </ServerRenderContext.Provider>
  );
};

export default App;
